import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "jalali-moment";
import { ListOfStatusGlobal } from "../../../../interface/enum/StatusGlobal";
import { useAuth } from "../../../../context/auth";

export interface WorkReportFilter {
  userRef: string;
  statusTask?: number;
  startDate?: string;
  endDate?: string;
}

interface WorkReportFilterSectionProps {
  filters: WorkReportFilter;
  showOnlyLatestLogs: boolean;
  userId: string;
  userName: string;
  onFiltersChange: (filters: WorkReportFilter) => void;
  onShowOnlyLatestLogsChange: (value: boolean) => void;
}

const WorkReportFilterSection: React.FC<WorkReportFilterSectionProps> = ({
  filters,
  showOnlyLatestLogs,
  userId,
  userName,
  onFiltersChange,
  onShowOnlyLatestLogsChange,
}) => {
  const { user } = useAuth();

  const handleStatusChange = (newStatus: number | undefined) => {
    console.log(`🔧 WorkReport Status Filter Changed:`, {
      timestamp: new Date().toISOString(),
      userId,
      userName,
      oldValue: filters.statusTask,
      newValue: newStatus,
      changedBy: user?.userName || "Unknown",
    });
    onFiltersChange({
      ...filters,
      statusTask: newStatus,
    });
  };

  const handleStartDateChange = (newValue: Date | null) => {
    if (newValue) {
      const formattedDate = moment(newValue).format("YYYY-MM-DD");
      console.log(`🔧 WorkReport Start Date Filter Changed:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        oldValue: filters.startDate,
        newValue: formattedDate,
        changedBy: user?.userName || "Unknown",
      });
      onFiltersChange({
        ...filters,
        startDate: formattedDate,
      });
    } else {
      console.log(`🔧 WorkReport Start Date Filter Cleared:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        oldValue: filters.startDate,
        newValue: "",
        changedBy: user?.userName || "Unknown",
      });
      onFiltersChange({
        ...filters,
        startDate: "",
      });
    }
  };

  const handleEndDateChange = (newValue: Date | null) => {
    if (newValue) {
      const formattedDate = moment(newValue).format("YYYY-MM-DD");
      console.log(`🔧 WorkReport End Date Filter Changed:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        oldValue: filters.endDate,
        newValue: formattedDate,
        changedBy: user?.userName || "Unknown",
      });
      onFiltersChange({
        ...filters,
        endDate: formattedDate,
      });
    } else {
      console.log(`🔧 WorkReport End Date Filter Cleared:`, {
        timestamp: new Date().toISOString(),
        userId,
        userName,
        oldValue: filters.endDate,
        newValue: "",
        changedBy: user?.userName || "Unknown",
      });
      onFiltersChange({
        ...filters,
        endDate: "",
      });
    }
  };

  const handleLatestLogsChange = (newValue: boolean) => {
    console.log(`🔧 WorkReport Latest Logs Filter Changed:`, {
      timestamp: new Date().toISOString(),
      userId,
      userName,
      oldValue: showOnlyLatestLogs,
      newValue,
      changedBy: user?.userName || "Unknown",
    });
    onShowOnlyLatestLogsChange(newValue);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
          تنظیمات فیلتر
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>وضعیت</InputLabel>
              <Select
                value={filters.statusTask ?? ""}
                label="وضعیت"
                displayEmpty
                renderValue={(selected) => {
                  const value = selected as string | number | undefined | null;
                  if (value === "" || value === undefined || value === null) {
                    return "همه وضعیت‌ها";
                  }
                  const status = ListOfStatusGlobal.find(
                    (s) => s.value === (value as number)
                  );
                  return status?.label || "نامشخص";
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  // اگر مقدار خالی است، undefined برگردان، در غیر این صورت به number تبدیل کن
                  const newStatus =
                    value === "" ? undefined : (value as number);
                  handleStatusChange(newStatus);
                }}
              >
                <MenuItem value="">همه وضعیت‌ها</MenuItem>
                {ListOfStatusGlobal.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="تاریخ شروع"
              value={filters.startDate ? new Date(filters.startDate) : null}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="تاریخ پایان"
              value={filters.endDate ? new Date(filters.endDate) : null}
              onChange={handleEndDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyLatestLogs}
                  onChange={(e) => handleLatestLogsChange(e.target.checked)}
                  color="primary"
                />
              }
              label="نمایش فقط آخرین لاگ وضعیتی هر کار"
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default WorkReportFilterSection;
